import React, { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DialogContent, Grid } from '@mui/material';
import {
  CirculationMultiplierDialog,
  DialogHeading,
  DialogMessage,
  SmallTextField,
  SummaryCalculationsDialogActions,
} from 'spw-components/dialogs/total-program/styles';
import { handleOnKeyPress } from 'spw-shared/utils';
import { convertToTwoDec } from 'spw-components/growth-calculator/utils';
import { globalInputLabelProps } from 'spw-shared/globalComponents/forms';
import { SPWSaveButton } from 'spw-shared/globalComponents/buttons';
import { SPWCancelButton } from 'spw-shared/globalComponents';
import { setSubtotalProgramMultiplier } from 'spw-redux/modules/calculators/actions';
import CirculationMultiplierTable from 'spw-components/programming-scenarios/building-hs-sc-scenario/circulation-multiplier-table';
import CalculationDefinitionsDialog from 'spw-components/programming-scenarios/calculation-definitions-dialog';
import { SummaryCalculator } from '../../../models/ProgramSummaryCalculator';

interface CirculationMultiplierProps {
  showDialog: boolean;
  setShowDialog?: (showDialog: boolean) => void;
  setEditFactor: (editFactor: string) => void;
  summaryCalculator: SummaryCalculator;
}

const CirculationMultiplierModal: React.FC<CirculationMultiplierProps> = ({
  summaryCalculator,
  showDialog,
  setShowDialog,
  setEditFactor,
}) => {
  const dispatch = useDispatch();

  const { subtotalMultiplierForCirculation: multiplier } = summaryCalculator;

  const [circulationMultiplier, setCirculationMultiplier] = useState(
    multiplier ? multiplier.toFixed(2) : '0.00'
  );

  const onBlur = () => {
    if (circulationMultiplier === '') {
      setCirculationMultiplier('0');
    } else if (
      parseFloat(circulationMultiplier) > 0 &&
      parseFloat(circulationMultiplier) < 0.25
    ) {
      setCirculationMultiplier('0.25');
    } else if (parseFloat(circulationMultiplier) > 3) {
      setCirculationMultiplier('3.00');
    }
  };

  const onClose = () => {
    setEditFactor('');
    setShowDialog(false);
  };

  const onSave = () => {
    dispatch(setSubtotalProgramMultiplier(parseFloat(circulationMultiplier)));
    onClose();
  };

  const handleMultiplierInputChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;
    setCirculationMultiplier(convertToTwoDec(inputValue));
  };

  return (
    <CirculationMultiplierDialog
      open={showDialog}
      onClose={onClose}
      disableScrollLock
    >
      <DialogContent>
        <Grid container>
          <Grid item xs={6}>
            <DialogHeading style={{ marginBottom: 32 }}>
              Circulation Multiplier
            </DialogHeading>

            <SmallTextField
              type="number"
              InputLabelProps={globalInputLabelProps}
              inputProps={{
                'data-testid': 'tps_circulation_multiplier',
              }}
              variant="outlined"
              value={circulationMultiplier}
              style={{ marginBottom: 44 }}
              onKeyPress={handleOnKeyPress}
              onChange={handleMultiplierInputChange}
              onBlur={onBlur}
            />

            <DialogMessage>
              <div className="warning">CAUTION</div>
              Changing the Circulation Multiplier of the Total Program Scenario
              will also update the Circulation Multiplier for all Department
              Scenarios linked to this summary.
            </DialogMessage>

            <SummaryCalculationsDialogActions>
              <SPWSaveButton onClick={onSave} />
              <SPWCancelButton onClick={onClose} />
            </SummaryCalculationsDialogActions>
          </Grid>
          <Grid item xs={6}>
            <DialogHeading>
              Circulation Multiplier Table <CalculationDefinitionsDialog />
            </DialogHeading>
            <CirculationMultiplierTable headerText="PERCENTAGE" gridSize={6} />
          </Grid>
        </Grid>
      </DialogContent>
    </CirculationMultiplierDialog>
  );
};

export default CirculationMultiplierModal;
